/**
 * Report Export Service
 *
 * PDF and CSV export utilities for reports.
 */

import { loadJsPDF } from '@/utils/exportLazyLoader';
import type {
  WiringReportData,
  WiringReportSection,
  PDFExportOptions,
  CableRequirementRow,
  CablePathDetailRow,
  JunctionBoxRow,
} from '@/types/reports';

// ============================================
// PDF Export
// ============================================

/**
 * Export wiring report to PDF.
 */
export async function exportWiringReportToPDF(
  data: WiringReportData,
  options: PDFExportOptions
): Promise<void> {
  const { jsPDF } = await loadJsPDF();

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Helper to check if we need a new page
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Add header
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('WIRING REPORT', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Add metadata
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Project: ${options.projectName}`, margin, yPosition);
  if (options.clientName) {
    pdf.text(`Client: ${options.clientName}`, pageWidth - margin, yPosition, { align: 'right' });
  }
  yPosition += 6;
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
  pdf.text(`Floor Plan: ${options.floorPlanFilter}`, pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 10;

  // Draw separator line (grayscale for better printing)
  pdf.setDrawColor(64, 64, 64); // dark gray
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Add summary section
  yPosition = addSummarySection(pdf, data, margin, yPosition, pageWidth);
  yPosition += 10;

  // Add cable requirements table
  checkNewPage(40);
  yPosition = addSectionHeader(pdf, 'Cable Requirements', margin, yPosition, pageWidth);
  yPosition = addCableRequirementsTable(pdf, data.cableRequirements, margin, yPosition, pageWidth, pageHeight);
  yPosition += 10;

  // Add path details table
  checkNewPage(40);
  yPosition = addSectionHeader(pdf, 'Path Details', margin, yPosition, pageWidth);
  yPosition = addPathDetailsTable(pdf, data.pathDetails, margin, yPosition, pageWidth, pageHeight);
  yPosition += 10;

  // Add junctions table
  checkNewPage(40);
  yPosition = addSectionHeader(pdf, 'Junction Boxes', margin, yPosition, pageWidth);
  yPosition = addJunctionsTable(pdf, data.junctions, margin, yPosition, pageWidth, pageHeight);

  // Save
  const fileName = `${options.projectName.replace(/[^a-z0-9]/gi, '_')}_Wiring_Report_${formatDate(new Date())}.pdf`;
  pdf.save(fileName);
}

function addSummarySection(
  pdf: any,
  data: WiringReportData,
  margin: number,
  yPosition: number,
  pageWidth: number
): number {
  const boxWidth = (pageWidth - margin * 2 - 10) / 3;
  const boxHeight = 20;

  // Draw summary boxes (3 cards)
  const summaryItems = [
    { label: 'TOTAL CABLE', value: `${data.summary.totalCableLength.toLocaleString()} ${data.summary.primaryUnit}` },
    { label: 'CABLE PATHS', value: data.summary.totalPaths.toString() },
    { label: 'JUNCTIONS', value: data.summary.totalJunctions.toString() },
  ];

  summaryItems.forEach((item, index) => {
    const xPos = margin + (boxWidth + 5) * index;

    // Box background
    pdf.setFillColor(249, 250, 251);
    pdf.setDrawColor(229, 231, 235);
    pdf.rect(xPos, yPosition, boxWidth, boxHeight, 'FD');

    // Label
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(107, 114, 128);
    pdf.text(item.label, xPos + boxWidth / 2, yPosition + 7, { align: 'center' });

    // Value
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(item.value, xPos + boxWidth / 2, yPosition + 15, { align: 'center' });
  });

  return yPosition + boxHeight + 5;
}

function addSectionHeader(
  pdf: any,
  title: string,
  margin: number,
  yPosition: number,
  pageWidth: number
): number {
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text(title, margin, yPosition);
  yPosition += 2;

  // Grayscale line for better printing
  pdf.setDrawColor(64, 64, 64); // dark gray
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);

  return yPosition + 6;
}

function addCableRequirementsTable(
  pdf: any,
  data: CableRequirementRow[],
  margin: number,
  yPosition: number,
  pageWidth: number,
  pageHeight: number
): number {
  if (data.length === 0) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(107, 114, 128);
    pdf.text('No cable requirements', margin, yPosition);
    return yPosition + 10;
  }

  const headers = ['Cable Type', 'Category', 'Total Length', 'Paths'];
  const colWidths = [70, 50, 40, 20];
  const rowHeight = 7;

  // Draw header
  pdf.setFillColor(243, 244, 246);
  pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);

  let xOffset = margin + 2;
  headers.forEach((header, i) => {
    pdf.text(header, xOffset, yPosition + 5);
    xOffset += colWidths[i];
  });
  yPosition += rowHeight;

  // Draw rows
  pdf.setFont('helvetica', 'normal');
  data.forEach((row, index) => {
    if (yPosition + rowHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
    }

    xOffset = margin + 2;
    pdf.text(truncateText(pdf, row.label, colWidths[0] - 4), xOffset, yPosition + 5);
    xOffset += colWidths[0];
    pdf.text(row.category, xOffset, yPosition + 5);
    xOffset += colWidths[1];
    pdf.text(`${row.total_length.toFixed(1)} ${row.unit}`, xOffset, yPosition + 5);
    xOffset += colWidths[2];
    pdf.text(row.path_count.toString(), xOffset, yPosition + 5);

    yPosition += rowHeight;
  });

  return yPosition;
}

function addPathDetailsTable(
  pdf: any,
  data: CablePathDetailRow[],
  margin: number,
  yPosition: number,
  pageWidth: number,
  pageHeight: number
): number {
  if (data.length === 0) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(107, 114, 128);
    pdf.text('No cable paths', margin, yPosition);
    return yPosition + 10;
  }

  const headers = ['Path', 'Floor Plan', 'Route', 'Length', 'Cables'];
  const colWidths = [25, 35, 25, 30, 65];
  const rowHeight = 7;

  // Draw header
  pdf.setFillColor(243, 244, 246);
  pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);

  let xOffset = margin + 2;
  headers.forEach((header, i) => {
    pdf.text(header, xOffset, yPosition + 5);
    xOffset += colWidths[i];
  });
  yPosition += rowHeight;

  // Draw rows
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  data.forEach((row, index) => {
    if (yPosition + rowHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
    }

    xOffset = margin + 2;
    pdf.text(truncateText(pdf, row.name, colWidths[0] - 4), xOffset, yPosition + 5);
    xOffset += colWidths[0];
    pdf.text(truncateText(pdf, row.floorPlanName, colWidths[1] - 4), xOffset, yPosition + 5);
    xOffset += colWidths[1];
    pdf.text(row.route, xOffset, yPosition + 5);
    xOffset += colWidths[2];
    pdf.text(`${row.length.toFixed(1)} ${row.unit}`, xOffset, yPosition + 5);
    xOffset += colWidths[3];
    // Show cables with qty × type = length
    pdf.text(truncateText(pdf, row.cablesDisplay, colWidths[4] - 4), xOffset, yPosition + 5);

    yPosition += rowHeight;
  });

  return yPosition;
}

function addJunctionsTable(
  pdf: any,
  data: JunctionBoxRow[],
  margin: number,
  yPosition: number,
  pageWidth: number,
  pageHeight: number
): number {
  if (data.length === 0) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(107, 114, 128);
    pdf.text('No junction boxes', margin, yPosition);
    return yPosition + 10;
  }

  const headers = ['Name', 'Floor Plan', 'Product', 'Paths', 'Notes'];
  const colWidths = [25, 40, 50, 25, 40];
  const rowHeight = 7;

  // Draw header
  pdf.setFillColor(243, 244, 246);
  pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);

  let xOffset = margin + 2;
  headers.forEach((header, i) => {
    pdf.text(header, xOffset, yPosition + 5);
    xOffset += colWidths[i];
  });
  yPosition += rowHeight;

  // Draw rows
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  data.forEach((row, index) => {
    if (yPosition + rowHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
    }

    xOffset = margin + 2;
    pdf.text(row.name, xOffset, yPosition + 5);
    xOffset += colWidths[0];
    pdf.text(truncateText(pdf, row.floorPlanName, colWidths[1] - 4), xOffset, yPosition + 5);
    xOffset += colWidths[1];
    pdf.text(truncateText(pdf, row.productName ?? '-', colWidths[2] - 4), xOffset, yPosition + 5);
    xOffset += colWidths[2];
    pdf.text(row.connectedPathCount.toString(), xOffset, yPosition + 5);
    xOffset += colWidths[3];
    pdf.text(truncateText(pdf, row.notes ?? '-', colWidths[4] - 4), xOffset, yPosition + 5);

    yPosition += rowHeight;
  });

  return yPosition;
}

function truncateText(pdf: any, text: string, maxWidth: number): string {
  if (pdf.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (pdf.getTextWidth(truncated + '...') > maxWidth && truncated.length > 0) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '...';
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// ============================================
// CSV Export
// ============================================

/**
 * Export wiring report section to CSV.
 */
export function exportWiringReportToCSV(
  data: WiringReportData,
  section: WiringReportSection,
  projectName: string
): void {
  let csvContent = '';
  let filename = '';

  switch (section) {
    case 'cable-requirements':
      csvContent = 'Cable Type,Category,Total Length,Unit,Path Count,Floor Plan\n';
      data.cableRequirements.forEach(row => {
        csvContent += `"${row.label}","${row.category}",${row.total_length},"${row.unit}",${row.path_count},"${row.floorPlanName ?? ''}"\n`;
      });
      filename = 'cable_requirements';
      break;

    case 'paths':
      csvContent = 'Path Name,Floor Plan,Start Junction,End Junction,Route,Type,Conduit,Path Length,Unit,Total Cable Qty,Total Cable Length,Cables Detail\n';
      data.pathDetails.forEach(row => {
        csvContent += `"${row.name}","${row.floorPlanName}","${row.startJunctionName ?? ''}","${row.endJunctionName ?? ''}","${row.route}","${row.pathType}","${row.conduitSpec ?? ''}",${row.length},"${row.unit}",${row.totalCableQty},${row.totalCableLength},"${row.cablesDisplay}"\n`;
      });
      filename = 'cable_paths';
      break;

    case 'junctions':
      csvContent = 'Name,Floor Plan,Product,Connected Paths,Notes\n';
      data.junctions.forEach(row => {
        csvContent += `"${row.name}","${row.floorPlanName}","${row.productName ?? ''}",${row.connectedPathCount},"${row.notes ?? ''}"\n`;
      });
      filename = 'junctions';
      break;
  }

  downloadCSV(csvContent, `${projectName.replace(/[^a-z0-9]/gi, '_')}_${filename}_${formatDate(new Date())}.csv`);
}

function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
